import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import DefaultDialog from "@/features/Common/components/DefaultDialog";
import FullScreenIframeDialog from "@/features/Common/components/FullScreenIframeDialog";
import Panel from "@/features/Common/components/Panel";
import StepsCounter from "@/features/Common/components/StepsCounter";
import { trackEvent, trackUserInteraction } from "@/services/analytics-adapter";
import {
  generatePlatformTsAndCsLinkBasedOnUserSelectedLanguage,
  getDefaultDocMedsBundlesAvailabilityData,
  MemberProductPaymentRatesChargeCode,
  MemberProfile,
  submitContractAgreement,
} from "@/services/core-api-adapter";
import theme, { defaultFullscreenPageStyling, shadows } from "@/theme";
import { convertPxToRem, formatAmount } from "@/utils";
import { Box, Checkbox, Divider, Link, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import ServiceAvailabilityList from "@/features/Common/components/ServiceAvailabilityList";
import { getConfigurationVariable } from "@/services/configuration-adapter";

interface DocMedsBundlesDisclaimerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function DocMedsBundlesDisclaimerModal({
  isOpen,
  onClose,
}: DocMedsBundlesDisclaimerModalProps) {
  const { t } = useTranslation();

  const [isTermsAndConditionsDialogOpen, setIsTermsAndConditionsDialogOpen] =
    useState(false);

  function onTermsLinkClick(event: any) {
    event.preventDefault();
    setIsTermsAndConditionsDialogOpen(true);
    trackEvent({
      event: "action.termsAndConditionsLinkOpened",
      source: "DocMeds Bundles Info",
    });
    trackUserInteraction({
      linkText: `DocMeds Bundles | Terms and Conditions`,
      linkIntent: "navigational",
      linkScope: "button",
    });
  }

  function onTermsDialogClose() {
    setIsTermsAndConditionsDialogOpen(false);
  }
  return (
    <DefaultDialog
      label={t("DocMedBundle.disclaimer.title")}
      isOpen={isOpen}
      onClose={onClose}
      fullWidth={true}
    >
      <Stack textAlign="center" spacing={2}>
        <Typography variant="body2" color="inherit">
          <Trans i18nKey="DocMedBundle.disclaimer.body" />
        </Typography>

        <Typography variant="body2" color="inherit">
          <Trans i18nKey="DocMedBundle.disclaimer.termsAndConditions">
            <Link
              sx={{ color: "primary", fontWeight: 900 }}
              href="/"
              onClick={onTermsLinkClick}
            ></Link>
          </Trans>
        </Typography>
      </Stack>

      <FullScreenIframeDialog
        title={t(
          "CollectTermsAcceptance.termsAndConditions.TERMS_AND_CONDITIONS_PLATFORM.documentTitle"
        )}
        isOpen={isTermsAndConditionsDialogOpen}
        onClose={onTermsDialogClose}
        url={generatePlatformTsAndCsLinkBasedOnUserSelectedLanguage()}
      />
    </DefaultDialog>
  );
}

interface DocMedsBundlesInfoProps {
  onBackButtonClick: () => void;
  onContinueButtonClick: () => void;
  contractsToSign: any;
  memberProfile: MemberProfile | null;
}

export default function DocMedsBundlesInfo({
  onBackButtonClick,
  onContinueButtonClick,
  contractsToSign,
  memberProfile,
}: DocMedsBundlesInfoProps) {
  const { t } = useTranslation();

  const defaultAvailabilityData = getDefaultDocMedsBundlesAvailabilityData();

  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  const [isTermsPreviouslyAccepted, setIsTermsPreviouslyAccepted] =
    useState(false);

  const [isGetStartedButtonEnabled, setIsGetStartedButtonEnabled] =
    useState(false);

  const [isDisclaimerModalOpen, setIsDisclaimerModalOpen] = useState(false);

  const [
    shouldShowEviaTermsAndConditionsCheckbox,
    setShouldShowEviaTermsAndConditionsCheckbox,
  ] = useState(true);

  function onViewDisclaimerClick() {
    setIsDisclaimerModalOpen(true);
  }

  const eviaTermsUrl =
    getConfigurationVariable("VITE_APP_UNU_HEALTH_EVIA_TERMS_CONDITIONS_URL") ||
    "";

  function onEviaTermsAndConditionsToggle(event: any) {
    event.preventDefault();
    setIsTermsAccepted(event.target.checked);

    trackEvent({
      event: "action.eviaTermsAndConditions",
      source: "DocMeds Bundles",
    });
    trackUserInteraction({
      linkText: `DocMeds Bundles | Evia Terms and Conditions accepted`,
      linkIntent: "confirmational",
      linkScope: "button",
    });
  }

  function getEviaTermsContract(contractsToSign: any) {
    if (contractsToSign && Array.isArray(contractsToSign.contracts)) {
      return (
        contractsToSign.contracts.find(
          (contract: any) =>
            contract.contractType === "TERMS_AND_CONDITIONS_EVIA_ZA"
        ) || null
      );
    }
    return null;
  }

  function getDigitalDoctorMedicineConsultAmount(
    memberProfile: any
  ): number | null {
    const offering = memberProfile?.offerings?.find(
      (o: any) =>
        o.chargeCode ===
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_MEDICINE_CONSULT
    );
    return offering ? offering.amount : null;
  }

  const price = getDigitalDoctorMedicineConsultAmount(memberProfile);

  function onGetStartedButtonClick() {
    trackEvent({
      event: "action.getStartedButtonClick",
      source: "DocMeds Bundles",
    });

    trackUserInteraction({
      linkText: `DocMeds Bundles | Get started button clicked`,
      linkIntent: "navigational",
      linkScope: "button",
    });

    if (isTermsAccepted === true && shouldShowEviaTermsAndConditionsCheckbox) {
      const eviaContract = getEviaTermsContract(contractsToSign);

      submitContractAgreement({
        contractType: eviaContract?.contractType,
        version: String(eviaContract?.version) || "",
      });

      onContinueButtonClick();
    } else {
      onContinueButtonClick();
    }
  }

  useEffect(() => {
    if (!contractsToSign || !Array.isArray(contractsToSign.contracts)) return;

    const hasAcceptedTheTermsAndConditionsPreviously =
      getEviaTermsContract(contractsToSign);

    setShouldShowEviaTermsAndConditionsCheckbox(
      Boolean(hasAcceptedTheTermsAndConditionsPreviously)
    );

    setIsTermsPreviouslyAccepted(!hasAcceptedTheTermsAndConditionsPreviously);
  }, [contractsToSign]);

  useEffect(() => {
    if (isTermsPreviouslyAccepted === true) {
      setIsGetStartedButtonEnabled(true);
    } else if (
      isTermsAccepted === true &&
      isTermsPreviouslyAccepted === false
    ) {
      setIsGetStartedButtonEnabled(true);
    }
  }, [isTermsAccepted, isTermsPreviouslyAccepted]);

  return (
    <>
      <Stack
        spacing={2}
        minHeight="100vh"
        position="relative"
        sx={{
          ...defaultFullscreenPageStyling,
          paddingBottom: convertPxToRem(130),
        }}
      >
        <CircledBackButton showLabel onClick={onBackButtonClick} />
        <Stack spacing={3}>
          <Panel sx={{ boxShadow: "none" }}>
            <Stack spacing={3}>
              <Stack spacing={1}>
                <Typography variant="h3" component="h1" fontWeight="bold">
                  {t("DocMedBundleInfo.title")}
                </Typography>
                <Typography variant="body1" color="neutral.main">
                  <Trans
                    i18nKey="DocMedBundleInfo.body"
                    values={{
                      price: formatAmount(price || 0, "ZAR"),
                    }}
                  />
                </Typography>
                <Typography variant="body1" color="neutral.main">
                  {t("DocMedBundleInfo.body2")}
                </Typography>
              </Stack>
              <Divider />
              <Stack spacing={2}>
                <Typography variant="h3" component="h2" fontWeight="bold">
                  {t("DocMedBundleInfo.whatHappensNext.title")}
                </Typography>
                <Stack direction="row">
                  <StepsCounter stepCounter={1} />
                  <Stack>
                    <Typography ml={2} variant="body1" fontWeight="bold">
                      {t("DocMedBundleInfo.step1.title")}
                    </Typography>
                    <Typography ml={2} variant="body1">
                      {t("DocMedBundleInfo.step1.body")}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction="row">
                  <StepsCounter stepCounter={2} />
                  <Stack>
                    <Typography ml={2} variant="body1" fontWeight="bold">
                      <Trans
                        i18nKey="DocMedBundleInfo.step2.title"
                        values={{
                          price: formatAmount(price || 0, "ZAR"),
                        }}
                      />
                    </Typography>
                    <Typography ml={2} variant="body1">
                      {t("DocMedBundleInfo.step2.body")}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction="row">
                  <StepsCounter stepCounter={3} />
                  <Stack>
                    <Typography ml={2} variant="body1" fontWeight="bold">
                      {t("DocMedBundleInfo.step3.title")}
                    </Typography>
                    <Typography ml={2} variant="body1">
                      {t("DocMedBundleInfo.step3.body")}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction="row">
                  <StepsCounter stepCounter={4} />
                  <Stack>
                    <Typography ml={2} variant="body1" fontWeight="bold">
                      {t("DocMedBundleInfo.step4.title")}
                    </Typography>
                    <Typography ml={2} variant="body1">
                      {t("DocMedBundleInfo.step4.body")}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction="row">
                  <StepsCounter stepCounter={5} />
                  <Stack>
                    <Typography ml={2} variant="body1" fontWeight="bold">
                      {t("DocMedBundleInfo.step5.title")}
                    </Typography>
                    <Typography ml={2} variant="body1">
                      <Trans i18nKey="DocMedBundleInfo.step5.body" />
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction="row">
                  <StepsCounter stepCounter={6} />
                  <Stack>
                    <Typography ml={2} variant="body1" fontWeight="bold">
                      {t("DocMedBundleInfo.step6.title")}
                    </Typography>
                    <Typography ml={2} variant="body1">
                      {t("DocMedBundleInfo.step6.body")}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Panel>
          <Divider />
          <Stack spacing={2}>
            <Typography component="h2" variant="h3" fontWeight="bold">
              {t("DocMedBundleInfo.partners.title")}
            </Typography>
            <Stack spacing={3}>
              <Stack direction="row" alignItems="center">
                <Box
                  src={`/assets/MedicalAssistancePartners/Intercare/logo-icon.svg`}
                  height={26}
                  alignSelf="flex-start"
                  component="img"
                  mr={1}
                  alt={t("DocMedBundleInfo.partners.intercare.title")}
                />
                <Stack>
                  <Typography variant="body1" fontWeight="bold">
                    {t("DocMedBundleInfo.partners.intercare.title")}
                  </Typography>
                  <Typography variant="body1">
                    {t("DocMedBundleInfo.partners.intercare.body")}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Box
                  src={`/assets/MedicalAssistancePartners/Evia/logo-icon.svg`}
                  height={26}
                  alignSelf="flex-start"
                  component="img"
                  mr={1}
                  alt={t("DocMedBundleInfo.partners.evia.title")}
                />
                <Stack>
                  <Typography variant="body1" fontWeight="bold">
                    {t("DocMedBundleInfo.partners.evia.title")}
                  </Typography>
                  <Typography variant="body1">
                    {t("DocMedBundleInfo.partners.evia.body")}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Box
                  src={`/assets/MedicalAssistancePartners/MrDelivery/logo-icon.svg`}
                  height={26}
                  alignSelf="flex-start"
                  component="img"
                  mr={1}
                  alt={t("DocMedBundleInfo.partners.mrD.title")}
                />
                <Stack>
                  <Typography variant="body1" fontWeight="bold">
                    {t("DocMedBundleInfo.partners.mrD.title")}
                  </Typography>
                  <Typography variant="body1">
                    {t("DocMedBundleInfo.partners.mrD.body")}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
            <ButtonWithAnalytics
              page="DocMeds Bundles Info"
              text={t(`common.viewDisclaimer`)}
              intent="navigational"
              onClick={onViewDisclaimerClick}
              variant="text"
            >
              {t("common.viewDisclaimer")}
            </ButtonWithAnalytics>
          </Stack>
          <Divider />

          <Stack
            sx={{
              padding: 2,
              backgroundColor: "primary.main",
              borderRadius: 2,
            }}
            spacing={2}
          >
            <Stack spacing={1}>
              <Typography
                variant="body1"
                color="background.paper"
                fontWeight="bold"
              >
                {t("DocMedBundleInfo.delivery.title")}
              </Typography>
              <Typography variant="body2" color="background.paper">
                <Trans i18nKey="DocMedBundleInfo.delivery.body" />
              </Typography>
            </Stack>
            <ServiceAvailabilityList
              sx={{
                color: theme.palette.neutral[50],
                fontSize: convertPxToRem(14),
              }}
              data={defaultAvailabilityData}
              boldDayLabels
            />
            <Typography variant="body2" color="background.paper">
              {t("DocMedBundleInfo.delivery.footer")}
            </Typography>
          </Stack>

          {shouldShowEviaTermsAndConditionsCheckbox ? (
            <Stack ml={2} direction="row" alignItems="center" spacing={2}>
              <Checkbox
                slotProps={{
                  input: {
                    "aria-label": t(
                      "DocMedBundleInfo.delivery.evia.termsConditions.ariaLabel"
                    ),
                  },
                }}
                sx={{ padding: 0 }}
                value={isTermsAccepted}
                onChange={onEviaTermsAndConditionsToggle}
              />
              <Stack direction="row">
                <Typography color="neutral">
                  <Trans i18nKey="DocMedBundleInfo.delivery.evia.termsConditions">
                    <Link
                      sx={{
                        color: "primary",
                        cursor: "pointer",
                        textDecoration: "none",
                      }}
                      href={eviaTermsUrl}
                      target="_blank"
                    ></Link>
                  </Trans>
                </Typography>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={1} textAlign="center">
              <Typography color="neutral">
                {t("DocMedBundle.disclaimer.termsAndConditions.accepted")}
              </Typography>
              <Typography color="neutral">
                <Trans i18nKey="DocMedBundle.disclaimer.termsAndConditions.accepted.footer">
                  <Link
                    sx={{
                      color: "primary",
                      cursor: "pointer",
                      textDecoration: "none",
                    }}
                    href={eviaTermsUrl}
                    target="_blank"
                  ></Link>
                </Trans>
              </Typography>
            </Stack>
          )}
        </Stack>

        <Stack
          position="fixed"
          display="flex"
          flexDirection="row"
          component="article"
          aria-label={t("common.startButton")}
          sx={{
            width: "100%",
            left: 0,
            bottom: 0,
            backgroundColor: "background.paper",
            boxShadow: shadows.heavy,
            alignItems: "center",
            padding: 2,
            borderTopLeftRadius: convertPxToRem(16),
            borderTopRightRadius: convertPxToRem(16),
            zIndex: 2,
          }}
        >
          <ButtonWithAnalytics
            page="DocMeds Bundles Info"
            text={t(`common.startButton`)}
            intent="confirmational"
            onClick={onGetStartedButtonClick}
            color="primary"
            fullWidth
            disabled={isGetStartedButtonEnabled === false}
          >
            {t("common.startButton")}
          </ButtonWithAnalytics>
        </Stack>
      </Stack>
      <DocMedsBundlesDisclaimerModal
        isOpen={isDisclaimerModalOpen}
        onClose={() => setIsDisclaimerModalOpen(false)}
      />
    </>
  );
}
